import { hasItems } from "@repo/utils";
import useSWR from "swr";

import * as fetcher from "../fetcher";
import { type PrePlanningDetail } from "../opportunity/types";

export interface HomeProjectAttachment {
  fileName: string;
  attachmentId: number;
  url: string;
  type: string;
  updatedDate: string;
  homeProjectId: string;
}

interface HomeProjectAttachmentResponse {
  files: HomeProjectAttachment[];
}

export interface ProductInfo {
  productNo: string;
  quantity: number;
}

export interface Inspiration {
  id: string;
  title: string;
  price?: {
    current: number;
  };
  currency?: string;
  roomId: string;
  gpcSceneId: string;
  pricingProducts: Record<string, ProductInfo[]>;
  image: {
    url: string;
    alt: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface InspirationDetailsResponse {
  code: number;
  data: Inspiration[];
  message: string;
}

interface DamProductInformation {
  itemNo: string;
  productName: string;
  productCategory: string;
  altText: string;
}

interface DamDigitalAsset {
  id: string;
  name: string;
  originalName?: string;
  altText: string;
  productCategory?: string;
  aspectRatio?: string;
  type?: string;
  original: string;
  width?: number;
  height?: number;
  datePublished?: string;
  dateCreated?: string;
  dateModified?: string;
  thumbnails?: Record<string, string>;
  productInformation?: DamProductInformation[];
  imAsset?: {
    url?: string;
  };
}

interface DamDigitalAssetsResponse {
  assets: DamDigitalAsset[];
}

const normalizeDamAssetToInspiration = (
  asset: DamDigitalAsset | undefined
): Inspiration | undefined => {
  if (!asset) {
    return undefined;
  }

  // Convert productInformation array to pricingProducts format
  const pricingProducts: Record<string, ProductInfo[]> = {};
  if (asset.productInformation && asset.productInformation.length > 0) {
    asset.productInformation.forEach((product) => {
      const category = product.productCategory || "Uncategorized";
      if (!pricingProducts[category]) {
        pricingProducts[category] = [];
      }
      pricingProducts[category].push({
        productNo: product.itemNo,
        quantity: 1, // DAM doesn't provide quantity, defaulting to 1
      });
    });
  }

  return {
    id: asset.id,
    title: asset.name,
    // price and currency are intentionally omitted for DAM assets
    // since pricing information is not available
    roomId: "",
    gpcSceneId: "",
    pricingProducts,
    image: {
      url: asset.original,
      alt: asset.altText,
    },
    createdAt: asset.dateCreated ?? "",
    updatedAt: asset.dateModified ?? "",
  };
};

export const useHomeProjectsAttachments = (
  homeProjectIds?: string[],
  customerPartyId?: string
) => {
  const stableIds = homeProjectIds ? [...homeProjectIds].sort() : [];
  const key =
    customerPartyId && hasItems(stableIds)
      ? [customerPartyId, ...stableIds]
      : null;

  const { data, error, isLoading } = useSWR<HomeProjectAttachment[]>(
    key,
    async () => {
      const results = await Promise.allSettled(
        stableIds.map((id) =>
          fetcher.global.get<HomeProjectAttachmentResponse>(
            `/home-project/${id}/customer/${customerPartyId}/files`
          )
        )
      );

      const allFiles: HomeProjectAttachment[] = results.flatMap((result) => {
        if (result.status === "fulfilled") {
          return result.value.files.map((file) => ({
            ...file,
            homeProjectId: stableIds[results.indexOf(result)],
          }));
        }
        return [];
      });

      return allFiles;
    }
  );

  return {
    data: data,
    error,
    isLoading,
  };
};

export const useInspirationDetails = (inspiration?: PrePlanningDetail) => {
  const sourceId = inspiration?.sourceId;
  const source = inspiration?.source;

  return useSWR<Inspiration | undefined>(
    sourceId && (source === "HIP" || source === "DAM")
      ? `${source}:${sourceId}`
      : null,
    async () => {
      if (!sourceId || !source) {
        return undefined;
      }

      if (source === "DAM") {
        const result = await fetcher.global.post<
          DamDigitalAssetsResponse,
          false
        >(`/dam/digital-assets`, {
          ids: [sourceId],
        });

        return normalizeDamAssetToInspiration(result.assets?.[0]);
      }

      const result = await fetcher.global.post<
        InspirationDetailsResponse,
        false
      >(`/hip/kitchen/scenes`, {
        ids: [sourceId],
      });

      return result.data?.[0];
    },
    {}
  );
};

export const shareAttachmentToHomeProject = async (
  attachmentId: number,
  shared: boolean
) => {
  return fetcher.opportunity.update(
    `/files/attachment/${attachmentId}/share?isSharedWithCustomerProject=${shared}`
  );
};
